import { Ticket } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFilterTickets } from '@/hooksApi';
import useLanguageStore from '@/stores/useLanguageStore';
import { formatDateByDayjs } from '@/utils';
import { ROUTE } from '@/config';
import { SectionHeader } from './SectionHeader';

interface TicketsSectionProps {
  platformCode?: string;
}

export function TicketsSection({ platformCode }: TicketsSectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { language } = useLanguageStore();

  const { data: ticketData, isFetching, isError } = useFilterTickets(
    language,
    1,
    10,
    undefined,
    platformCode,
  );

  const tickets = ticketData?.object?.tickets ?? [];

  if (!platformCode) return null;
  // Bug 7: Show section even on error, with graceful fallback
  if (!isFetching && !isError && tickets.length === 0) return null;

  return (
    <div className='rounded-2xl border border-sd-border bg-sd-card p-5'>
      <SectionHeader icon={Ticket} title={String(t('tickets'))} />

      {isFetching ? (
        <div className='flex items-center justify-center py-6'>
          <div className='h-4 w-4 rounded-full border-2 border-sd-primary border-t-transparent animate-spin' />
        </div>
      ) : isError ? (
        <p className='text-sm text-sd-muted-foreground py-4 text-center'>
          {String(t('tickets_load_error', { defaultValue: 'Unable to load tickets' }))}
        </p>
      ) : (
        <div className='flex flex-col gap-3'>
          {tickets.map((ticket) => {
            const ticketId = ticket.id || ticket.encoded_id;
            const isOpen = ticket.status === 'open' || ticket.status === 'new' || ticket.status === 'pending';
            return (
              <button
                key={ticketId}
                onClick={() => navigate(`${ROUTE.TICKET_CENTER}/details/${ticketId}`)}
                className='rounded-xl border border-sd-border bg-sd-card px-4 py-3.5 text-start cursor-pointer hover:bg-sd-muted/50 transition-colors w-full'
              >
                <div className='flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <span className='text-[13px] font-bold text-sd-foreground'>
                      {String(ticketId)}
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                        isOpen
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {isOpen ? String(t('open')) : String(t('closed'))}
                    </span>
                  </div>
                  <span className='text-[12px] text-sd-muted-foreground'>
                    {formatDateByDayjs(ticket.created_at, 'D MMM YYYY')}
                  </span>
                </div>
                {ticket.subject && (
                  <p className='text-[13px] text-sd-muted-foreground mt-1 m-0'>{ticket.subject}</p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
